import {Component, HostListener, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {City} from '@models/city';
import {LabelType, Options} from '@angular-slider/ngx-slider';
import {CityParams, UserSearchCity} from '@models/search';
import {AdService} from '@services/ad.service';
import {UserService} from '@services/user.service';
import {AlertService} from '@services/alert.service';
import {CommonService} from '@services/common.service';
import {CityService} from '@services/city.service';
import {MatDialog} from '@angular/material/dialog';
import {Location} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationService} from '@services/authentification.service';
import {TrackingService} from '@services/tracking.service';
import {Subject} from 'rxjs';
import {debounceTime, startWith, switchMap} from 'rxjs/operators';
import * as $ from 'jquery';
import {TutoComponent} from '@components/popup/tuto/tuto.component';
import {OnboardingService} from '@services/onboarding.service';
import {UpgradePlanV2Component} from '@components/popup/upgrade-plan-v2/upgrade-plan-v2.component';
import {SEARCH_CITY_PARAMS} from '@models/empty';
import {FiltersDrawerBase} from '../filters-drawer-base';

@Component({
    selector: 'app-filters-city',
    templateUrl: './filters-city.component.html',
    styleUrls: ['./filters-city.component.scss']
})
export class FiltersCityComponent extends FiltersDrawerBase<CityParams> implements OnInit {

    searchForm: UntypedFormGroup;
    citiesCount: number = null;

    // Mobile (page filtres) : entrer dans une catégorie pousse une entrée d'historique,
    // pour que le retour natif (geste du bord gauche / bouton back) ramène à la liste
    // des catégories au lieu de quitter la page (le navigateur intercepte le geste du
    // bord avant nos handlers touch : impossible de le gérer nous-mêmes)
    private modeValue = 'sidebar';
    private modeFromPopState = false;
    private skipNextPopState = false;

    get mode(): string {
        return this.modeValue;
    }

    set mode(m: string) {
        if (m === this.modeValue) {
            return;
        }
        if (window.innerWidth < 992 && !this.drawer) {
            if (m === 'filtres') {
                history.pushState({lyboxFiltres: true}, '');
            } else if (this.modeValue === 'filtres' && !this.modeFromPopState && history.state && history.state.lyboxFiltres) {
                // Retour déclenché par l'UI (flèche, swipe) : consommer l'entrée poussée
                this.skipNextPopState = true;
                history.back();
            }
        }
        this.modeValue = m;
    }

    @HostListener('window:popstate')
    onPopState() {
        if (this.skipNextPopState) {
            this.skipNextPopState = false;
            return;
        }
        if (window.innerWidth < 992 && !this.drawer && this.modeValue === 'filtres') {
            this.modeFromPopState = true;
            this.mode = 'sidebar';
            this.modeFromPopState = false;
        }
    }

    options: Options = {
        floor: 0,
        ceil: 1000000,
        step: 10000,
        translate: (value: number, label: LabelType): string => {
            if (value === 1000000) {
                return '1 000 000 € et +';
            }
            return value + ' €';
        }
    };

    optionsPop: Options = {
        floor: 0,
        ceil: 150000,
        step: 1000,
        translate: (value: number, label: LabelType): string => {
            if (value === 150000) {
                return '150 000 hab et +';
            }
            return value + ' hab';
        }
    };

    optionsDistance: Options = {
        stepsArray: [
            {value: 0}, {value: 3}, {value: 5},
            {value: 10}, {value: 20}, {value: 30}, {value: 40}, {value: 50},
            {value: 60}, {value: 70}, {value: 80}, {value: 90}, {value: 100}
        ],
        translate: (value: number, label: LabelType): string => {
            return value + ' km';
        }
    };

    // Croissance démographique : liste à cocher (mêmes valeurs que l'ancien select multiple)
    evolDemoOptions = [
        {value: '-1', label: 'Décroissance'},
        {value: '0', label: 'Stable'},
        {value: '1', label: 'Croissance'},
    ];

    isEvolDemoSelected(value: string): boolean {
        return (this.searchForm.get('evol_demo').value || []).includes(value);
    }

    toggleEvolDemo(value: string) {
        if (!this.authenticationService.isAllowed('essentiel')) {
            this.unlock();
            return;
        }
        const current = this.searchForm.get('evol_demo').value || [];
        this.searchForm.get('evol_demo').setValue(
            current.includes(value) ? current.filter(v => v !== value) : current.concat([value])
        );
        this.countChange$.next();
    }

    // Rayon en slider : mêmes crans que optionsDistance, rendu compact
    // (libellé « Rayon : X km » porté par le template, pas par le slider)
    kmSliderOptions: Options = {
        ...this.optionsDistance,
        hideLimitLabels: true,
        hidePointerLabels: true,
        showSelectionBar: true,
    };

    optionsMv: Options = {
        floor: 0,
        ceil: 40000,
        step: 500,
        translate: (value: number, label: LabelType): string => {
            if (value === 40000) {
                return '40 000 € et +';
            }
            return value + ' €';
        }
    };

    optionsTc: Options = {
        floor: 0,
        ceil: 30,
        translate: (value: number, label: LabelType): string => {
            if (value === 30) {
                return '30 % et +';
            }
            return value + ' %';
        }
    };

    optionsTe: Options = {
        floor: 0,
        ceil: 30,
        translate: (value: number, label: LabelType): string => {
            if (value === 30) {
                return '30 % et +';
            }
            return value + ' %';
        }
    };

    optionsTv: Options = {
        floor: 0,
        ceil: 30,
        translate: (value: number, label: LabelType): string => {
            if (value === 30) {
                return '30 % et +';
            }
            return value + ' %';
        }
    };

    optionsTl: Options = {
        floor: 0,
        ceil: 80,
        translate: (value: number, label: LabelType): string => {
            if (value === 80) {
                return '80 % et +';
            }
            return value + ' %';
        }
    };

    // Libellés qualitatifs de la tension locative (0 = très facile ... 8 = très difficile)
    static readonly TENSIOMETRE_LABELS = ['Très facile', 'Moy facile', 'Facile', 'Normal', 'Normal', 'Normal', 'Difficile', 'Moy difficile', 'Très difficile'];

    optionsTensiometre: Options = {
        floor: 0,
        ceil: 8,
        translate: (value: number, label: LabelType): string =>
            FiltersCityComponent.TENSIOMETRE_LABELS[value] || ''
    };

    // Selects Min/Max de la tension locative : libellés dédupliqués (un seul « Normal »),
    // et filtrage croisé (le min ne propose pas au-delà du max, et inversement)
    getTensiometreBound(index: number): number {
        const v = this.searchForm.get('tensiometre').value;
        const raw = v && v.length && v[index] != null ? v[index] : (index === 0 ? 0 : 8);
        // Valeur canonique du libellé (ex. 4 « Normal » -> 3 pour un min, 5 pour un max),
        // pour matcher les options dédupliquées même sur d'anciennes valeurs de slider
        const labels = FiltersCityComponent.TENSIOMETRE_LABELS;
        return index === 0 ? labels.indexOf(labels[raw]) : labels.lastIndexOf(labels[raw]);
    }

    setTensiometreBound(index: number, value: number) {
        const bounds = [this.getTensiometreBound(0), this.getTensiometreBound(1)];
        bounds[index] = value;
        this.searchForm.get('tensiometre').setValue(bounds);
        this.countChange$.next();
    }

    // Mémoïsation : le getter doit renvoyer le MÊME tableau tant que la borne ne change pas,
    // sinon mat-select re-synchronise en boucle (nouvelle identité à chaque cycle de CD)
    private tensioCache = {min: null, max: null, minOpts: [], maxOpts: []};

    get tensiometreMinOptions() {
        const max = this.getTensiometreBound(1);
        if (this.tensioCache.max !== max) {
            this.tensioCache.max = max;
            const opts = [];
            FiltersCityComponent.TENSIOMETRE_LABELS.forEach((label, v) => {
                // min : première valeur de chaque libellé
                if (v <= max && !opts.find(o => o.label === label)) {
                    opts.push({value: v, label});
                }
            });
            this.tensioCache.minOpts = opts;
        }
        return this.tensioCache.minOpts;
    }

    get tensiometreMaxOptions() {
        const min = this.getTensiometreBound(0);
        if (this.tensioCache.min !== min) {
            this.tensioCache.min = min;
            const opts = [];
            FiltersCityComponent.TENSIOMETRE_LABELS.forEach((label, v) => {
                // max : dernière valeur de chaque libellé
                if (v >= min) {
                    const existing = opts.find(o => o.label === label);
                    if (existing) {
                        existing.value = v;
                    } else {
                        opts.push({value: v, label});
                    }
                }
            });
            this.tensioCache.maxOpts = opts;
        }
        return this.tensioCache.maxOpts;
    }

    optionsSquarePriceCity: Options = {
        floor: 0,
        ceil: 10000,
        step: 100,
        translate: (value: number, label: LabelType): string => {
            if (value === 10000) {
                return '10000 €/m² et +';
            }
            return value + ' €/m²';
        }
    };

    optionsEfficiencyCity: Options = {
        floor: 0,
        ceil: 10,
        step: 0.5,
        translate: (value: number, label: LabelType): string => {
            if (value === 10) {
                return '10 % et +';
            }
            return value + ' %';
        }
    };

    optionsPriceEvolCity: Options = {
        floor: -100,
        ceil: 100,
        step: 5,
        translate: (value: number, label: LabelType): string => {
            if (value === 100) {
                return '100 % et +';
            } else if (value === -100) {
                return '- 100 % et -';
            }
            return value + ' %';
        }
    };

    optionsSquareRent: Options = {
        floor: 0,
        ceil: 50,
        translate: (value: number, label: LabelType): string => {
            if (value === 50) {
                return '50 €/m² et +';
            }
            return value + ' €/m²';
        }
    };

    zipCode: City[] = [];
    cities_include: City[] = [];
    cities_exclude: City[] = [];
    params;

    // region: [
    //     {
    //         name: 'Auvergne-Rhône-Alpes',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Bourgogne-Franche-Comté',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Bretagne',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Centre-Val de Loire',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Corse',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Grand Est',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Guadeloupe',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Guyane',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Hauts-de-France',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Ile-de-France',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'La Réunion',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Martinique',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Mayotte',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Normandie',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Nouvelle-Aquitaine',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Occitanie',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Pays de la Loire',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    //     {
    //         name: 'Provence-Alpes-Côte d\'Azur',
    //         completed: false,
    //         subtasks: [
    //             { name: 'Primary', completed: false },
    //         ],
    //     },
    // ];

    currentSearch: UserSearchCity;
    userSearchs: UserSearchCity[] = [];
    currentTab = 'city_include';

    constructor(
        private adService: AdService,
        private userService: UserService,
        private alertService: AlertService,
        public commonService: CommonService,
        public cityService: CityService,
        public onboardingService: OnboardingService,
        public dialog: MatDialog,
        private location: Location,
        private http: HttpClient,
        private route: ActivatedRoute,
        public authenticationService: AuthenticationService,
        public trackingService: TrackingService,
        private router: Router) {
        super();
    }

    // Le plumbing du mode bandeau (drawer/paramsChange/liveChange$...) vient de FiltersDrawerBase

    // Compteur de villes : déclenché via ce flux (debounce 400ms), jamais en direct
    private countChange$ = new Subject<void>();

    ngOnInit(): void {
        // Règle projet : debounce ~400ms avant l'API — les changements de filtres
        // (frappe dans un champ Min/Max, cases...) passent par ce flux, jamais en direct
        this.countChange$.pipe(debounceTime(400)).subscribe(() => this.loadCities());
        this.params = {'id': ''};
        if (this.drawer) {
            // Mode bandeau : pas de lecture de la route (on est sur la page résultats)
            this.params = {id: this.drawerSearchId != null ? this.drawerSearchId : 'result'};
            if (this.authenticationService.isLogin) {
                this.adService.getUserSearchCity().subscribe(r => {
                    if (this.authenticationService.isAllowed('essentiel')) {
                        this.userSearchs = r;
                    }
                    this.loadSearchById();
                });
            } else {
                this.loadSearchById();
            }
            this.setSearchForm();
            this.liveChange$.pipe(debounceTime(1000)).subscribe(() => this.emitLiveParams());
            return;
        }
        if (this.authenticationService.isLogin) {
            this.adService.getUserSearchCity().subscribe(r => {
                if (this.authenticationService.isAllowed('essentiel')) {
                    this.userSearchs = r;
                }
                this.setParams();
                this.setSearch();
            });
        } else {
            this.setParams();
        }
        this.setSearchForm();
        this.onboardingService.listen().subscribe(elem => {

        });
        if (this.authenticationService.currentUser.user_onboarding.auto && this.authenticationService.currentUser.user_onboarding.ville.status == 'not_started') {
            this.onboardingService.startVille().subscribe();
        }
    }

    setParams() {
        this.route.params.subscribe(params => {
            this.params = params;
            this.loadSearchById();
        });
    }

    // Chargement de la recherche courante / enregistrée (page filtres et mode bandeau)
    private loadSearchById() {
        if (this.params.id !== undefined) {
            if (this.params.id == 'result') {
                this.userService.getCurrentSearchCity().subscribe(params => {
                    this.currentSearch = params;
                    this.setSearchForm(); // Set values in BDD
                    this.currentSearch.search_city_params = this.getSearchParams(); // Get all value and default value if missing on DB
                    this.setSearchForm(); // Set again with all fields
                    if (this.drawer) {
                        this.lastEmittedParams = JSON.stringify(this.getSearchParams());
                    }
                });
            } else {
                this.setSearch();
                if (this.drawer) {
                    this.lastEmittedParams = JSON.stringify(this.getSearchParams());
                }
            }
        }
    }

    // Écriture des paramètres pour le live update de FiltersDrawerBase
    protected storeParams(params: CityParams) {
        this.currentSearch.search_city_params = params;
    }

    setSearch() {
        if (this.params.id !== undefined && this.userSearchs.length > 0) {
            if (this.authenticationService.isAllowed('essentiel')) {
                this.userSearchs.forEach(f => {
                    if (f.id === parseInt(this.params.id, 10)) {
                        this.currentSearch = f;
                        this.setSearchForm(); // Set values in BDD
                        this.currentSearch.search_city_params = this.getSearchParams(); // Get all value and default value if missing on DB
                        this.setSearchForm(); // Set again with all fields
                    }
                });
            } else {
                this.alertService.error('Cette fonctionnalité nécessite un plan supérieur, consultez nos forfaits dans Mon compte > Nos forfaits');
            }
        }
    }

    setSearchForm() {
        const unlock = this.authenticationService.isAllowed('essentiel');

        this.optionsPop.disabled = !unlock;
        this.optionsMv.disabled = !unlock;
        this.optionsTe.disabled = !unlock;
        this.optionsTv.disabled = !unlock;
        this.optionsTl.disabled = !unlock;
        this.optionsTc.disabled = false;
        this.optionsTensiometre.disabled = !unlock;
        this.optionsSquarePriceCity.disabled = !unlock;
        this.optionsSquareRent.disabled = !unlock;
        this.optionsEfficiencyCity.disabled = !unlock;
        this.optionsPriceEvolCity.disabled = !unlock;

        this.searchForm = new UntypedFormGroup({
            //  exclude_cities: new UntypedFormControl({
            //     value: this.currentSearch ? this.currentSearch.search_city_params.exclude_cities : null,
            //     disabled: !unlock
            // }),
            cityIncludeControl: new UntypedFormControl({value: null}),
            cityExcludeControl: new UntypedFormControl({value: null}),
            region: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.region != null && this.currentSearch.search_city_params.region.length ? this.currentSearch.search_city_params.region : null,
                disabled: !unlock
            }),
            departement: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.departement != null && this.currentSearch.search_city_params.departement.length ? this.currentSearch.search_city_params.departement : null,
                disabled: !unlock
            }),
            evol_demo: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.evol_demo != null && this.currentSearch.search_city_params.evol_demo.length ? this.currentSearch.search_city_params.evol_demo : null,
                disabled: !unlock
            }),
            pop: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.pop != null && this.currentSearch.search_city_params.pop.length ? this.currentSearch.search_city_params.pop : null,
                disabled: this.optionsPop.disabled
            }),
            mv: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.mv != null && this.currentSearch.search_city_params.mv.length ? this.currentSearch.search_city_params.mv : null,
                disabled: this.optionsMv.disabled
            }),
            tc: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.tc != null && this.currentSearch.search_city_params.tc.length ? this.currentSearch.search_city_params.tc : null,
                disabled: this.optionsTc.disabled
            }),
            te: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.te != null && this.currentSearch.search_city_params.te.length ? this.currentSearch.search_city_params.te : null,
                disabled: this.optionsTe.disabled
            }),
            tv: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.tv != null && this.currentSearch.search_city_params.tv.length ? this.currentSearch.search_city_params.tv : null,
                disabled:  this.optionsTv.disabled
            }),
            tl: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.tl != null && this.currentSearch.search_city_params.tl.length ? this.currentSearch.search_city_params.tl : null,
                disabled: this.optionsTl.disabled
            }),
            squarePriceCity: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.squarePriceCity != null && this.currentSearch.search_city_params.squarePriceCity.length ? this.currentSearch.search_city_params.squarePriceCity : null,
                disabled: this.optionsSquarePriceCity.disabled
            }),
            efficiencyCity: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.efficiencyCity != null && this.currentSearch.search_city_params.efficiencyCity.length ? this.currentSearch.search_city_params.efficiencyCity : null,
                disabled:  this.optionsEfficiencyCity.disabled
            }),
            priceEvolCity_1: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.priceEvolCity_1 != null && this.currentSearch.search_city_params.priceEvolCity_1.length ? this.currentSearch.search_city_params.priceEvolCity_1 : null,
                disabled: this.optionsPriceEvolCity.disabled
            }),
            priceEvolCity_3: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.priceEvolCity_3 != null && this.currentSearch.search_city_params.priceEvolCity_3.length ? this.currentSearch.search_city_params.priceEvolCity_3 : null,
                disabled: this.optionsPriceEvolCity.disabled
            }),
            priceEvolCity_5: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.priceEvolCity_5 != null && this.currentSearch.search_city_params.priceEvolCity_5.length ? this.currentSearch.search_city_params.priceEvolCity_5 : null,
                disabled: this.optionsPriceEvolCity.disabled
            }),
            priceEvolCity_10: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.priceEvolCity_10 != null && this.currentSearch.search_city_params.priceEvolCity_10.length ? this.currentSearch.search_city_params.priceEvolCity_10 : null,
                disabled: this.optionsPriceEvolCity.disabled
            }),
            squareRent: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.squareRent != null && this.currentSearch.search_city_params.squareRent.length ? this.currentSearch.search_city_params.squareRent : null,
                disabled: this.optionsSquareRent.disabled
            }),
            tensiometre: new UntypedFormControl({
                value: this.currentSearch && this.currentSearch.search_city_params.tensiometre != null && this.currentSearch.search_city_params.tensiometre.length ? this.currentSearch.search_city_params.tensiometre : null,
                disabled: this.optionsTensiometre.disabled
            }),
            coeur_de_ville: new UntypedFormControl({
                value: this.currentSearch ? this.currentSearch.search_city_params.coeur_de_ville : null,
                disabled: !unlock
            }),
            denormandie: new UntypedFormControl({
                value: this.currentSearch ? this.currentSearch.search_city_params.denormandie : null,
                disabled: !unlock
            }),
        });

        this.cities_include = this.currentSearch && this.currentSearch.search_city_params.cities_include ? this.currentSearch.search_city_params.cities_include : [];
        this.cities_exclude = this.currentSearch && this.currentSearch.search_city_params.cities_exclude ? this.currentSearch.search_city_params.cities_exclude : [];

        this.searchForm.get('cityIncludeControl').valueChanges
            .pipe(
                startWith(''),
                debounceTime(500),
                switchMap(value => this.cityService.search(this.searchForm.get('cityIncludeControl').value))
            )
            .subscribe(d => {
                $('.citySpinner').hide();
                this.zipCode = d;
            });

        this.searchForm.get('cityExcludeControl').valueChanges
            .pipe(
                startWith(''),
                debounceTime(500),
                switchMap(value => this.cityService.search(this.searchForm.get('cityExcludeControl').value))
            )
            .subscribe(d => {
                $('.citySpinner').hide();
                this.zipCode = d;
            });

        // Compteur de villes à jour pour TOUT changement de filtre (cases, selects, sliders...),
        // pas seulement les chemins qui notifient manuellement. Les champs texte d'autocomplete
        // ne modifient pas les paramètres de recherche -> exclus (sinon un appel API par frappe)
        Object.keys(this.searchForm.controls)
            .filter(k => k !== 'cityIncludeControl' && k !== 'cityExcludeControl')
            .forEach(k => this.searchForm.get(k).valueChanges.subscribe(() => this.countChange$.next()));
        this.countChange$.next();
    }

    unlock() {
        const dialogRef = this.dialog.open(UpgradePlanV2Component, {
            width: '520px',
            data: {visuel: 'assets/upgrade/map-city-search.png', plan: 'Essentiel', feature: 'Recherche des villes rentables'}
        });
    }

    loadCities() {
        const params = this.getSearchParams();

        this.adService.countCities(params).subscribe(res => {
            this.citiesCount = res.count;
        }, error => {
            this.alertService.error('Une erreur est survenue');
            console.log(error);
        });
    }

    getSearchParams(): CityParams {
        const cities_include = [];
        this.cities_include.forEach(c => {
            cities_include.push(c);
        });

        const cities_exclude = [];
        this.cities_exclude.forEach(c => {
            cities_exclude.push(c);
        });

        return {
            // exclude_cities: this.searchForm.get('exclude_cities').value || false,
            region: this.searchForm.get('region').value || [],
            departement: this.searchForm.get('departement').value || [],
            evol_demo: this.searchForm.get('evol_demo').value || [],
            pop: this.searchForm.get('pop').value || [],
            tc: this.searchForm.get('tc').value || [],
            te: this.searchForm.get('te').value || [],
            tv: this.searchForm.get('tv').value || [],
            mv: this.searchForm.get('mv').value || [],
            tl: this.searchForm.get('tl').value || [],
            coeur_de_ville: this.searchForm.get('coeur_de_ville').value || false,
            denormandie: this.searchForm.get('denormandie').value || false,
            squareRent: this.searchForm.get('squareRent').value || [],
            tensiometre: this.searchForm.get('tensiometre').value || [],
            squarePriceCity: this.searchForm.get('squarePriceCity').value || [],
            efficiencyCity: this.searchForm.get('efficiencyCity').value || [],
            priceEvolCity_1: this.searchForm.get('priceEvolCity_1').value || [],
            priceEvolCity_3: this.searchForm.get('priceEvolCity_3').value || [],
            priceEvolCity_5: this.searchForm.get('priceEvolCity_5').value || [],
            priceEvolCity_10: this.searchForm.get('priceEvolCity_10').value || [],
            cities_include,
            cities_exclude,
            // conserve le tri choisi sur la liste des résultats
            sort: this.currentSearch && this.currentSearch.search_city_params ? this.currentSearch.search_city_params.sort : undefined,
            sort_order: this.currentSearch && this.currentSearch.search_city_params ? this.currentSearch.search_city_params.sort_order : undefined,
        };
    }

    addCity(value, mode) {
        value.km = 0;
        if (mode == 'include') {
            this.cities_include.push(value);
            this.searchForm.get('cityIncludeControl').setValue('');
        } else {
            this.cities_exclude.push(value);
            this.searchForm.get('cityExcludeControl').setValue('');
        }
        this.countChange$.next();
    }

    removeCity(value, mode) {
        if (mode == 'include') {
            const index = this.cities_include.indexOf(value, 0);
            if (index > -1) {
                this.cities_include.splice(index, 1);
            }
        } else {
            const index = this.cities_exclude.indexOf(value, 0);
            if (index > -1) {
                this.cities_exclude.splice(index, 1);
            }
        }
        this.countChange$.next();
    }

    // Champs de search_city_params portés par chaque catégorie de filtres (pour l'effacement par section)
    private static readonly TAB_FIELDS = {
        city_include: ['cities_include'],
        city_exclude: ['cities_exclude'],
        localisation: ['region', 'departement', 'coeur_de_ville', 'denormandie'],
        demographie: ['pop', 'tc', 'te', 'tv', 'mv', 'evol_demo'],
        market: ['tensiometre', 'tl', 'squareRent', 'squarePriceCity', 'efficiencyCity'],
        price_evol: ['priceEvolCity_1', 'priceEvolCity_3', 'priceEvolCity_5', 'priceEvolCity_10'],
    };

    // Effacer (liste des catégories) : remet TOUS les filtres par défaut et lance la recherche
    resetAllFilters() {
        this.currentSearch.search_city_params = JSON.parse(JSON.stringify(SEARCH_CITY_PARAMS));
        this.setSearchForm();
        this.search();
    }

    // Effacer (dans une catégorie) : remet par défaut uniquement les filtres de la section
    // courante, SANS quitter la section (le retour est porté par la flèche du bandeau)
    resetCurrentTab() {
        const defaults = JSON.parse(JSON.stringify(SEARCH_CITY_PARAMS));
        (FiltersCityComponent.TAB_FIELDS[this.currentTab] || []).forEach(f => {
            this.currentSearch.search_city_params[f] = defaults[f];
        });
        this.setSearchForm();
    }

    // Libellés des sections, repris du menu des catégories : affichés dans le bandeau
    // de la section ouverte, pour savoir dans quel filtre on se trouve
    private static readonly TAB_LABELS = {
        city_include: 'Villes à inclure',
        city_exclude: 'Villes à exclure',
        localisation: 'Localisation',
        demographie: 'Filtres socio-démographiques',
        market: 'Marché immobilier',
        price_evol: 'Évolution des prix',
    };

    getTabLabel(): string {
        return FiltersCityComponent.TAB_LABELS[this.currentTab] || '';
    }

    // Mobile : glissement depuis le bord gauche dans une catégorie -> retour à la liste (geste natif)
    private touchStartX: number = null;
    private touchStartY: number = null;

    @HostListener('touchstart', ['$event'])
    onTouchStart(e: TouchEvent) {
        this.touchStartX = null;
        if (window.innerWidth >= 992 || this.mode !== 'filtres') {
            return;
        }
        const t = e.touches[0];
        // Uniquement depuis le bord gauche, et jamais sur un slider (dont la poignée peut être au bord)
        if (t.clientX <= 30 && !(e.target as HTMLElement).closest('.ngx-slider')) {
            this.touchStartX = t.clientX;
            this.touchStartY = t.clientY;
        }
    }

    @HostListener('touchend', ['$event'])
    onTouchEnd(e: TouchEvent) {
        if (this.touchStartX === null) {
            return;
        }
        const t = e.changedTouches[0];
        const dx = t.clientX - this.touchStartX;
        const dy = Math.abs(t.clientY - this.touchStartY);
        this.touchStartX = null;
        if (dx > 60 && dy < 50) {
            this.mode = 'sidebar';
        }
    }

    // Rayon autour d'une ville : menu déroulant à côté du nom
    get distanceValues(): number[] {
        return (this.optionsDistance.stepsArray || []).map(s => s.value);
    }

    // Bornes Min/Max des filtres à plage : null = pas de borne
    getRangeBound(controlName: string, index: number): number {
        const value = this.searchForm.get(controlName).value;
        return value && value.length && value[index] != null && value[index] !== '' ? value[index] : null;
    }

    // Valeur affichée dans les inputs : vide uniquement quand la borne est absente (null = pas de filtre)
    getRangeBoundDisplay(controlName: string, index: number) {
        return this.getRangeBound(controlName, index);
    }

    setRangeBound(controlName: string, index: number, raw: any) {
        const parsed = parseFloat(raw);
        const range = [this.getRangeBound(controlName, 0), this.getRangeBound(controlName, 1)];
        range[index] = isNaN(parsed) ? null : parsed;
        this.searchForm.get(controlName).setValue(range[0] === null && range[1] === null ? null : range);
        this.countChange$.next();
    }

    search() {
        this.currentSearch.search_city_params = this.getSearchParams();
        this.onboardingService.endVille(1, 1);
        if (this.drawer) {
            this.drawerSearch(this.currentSearch.search_city_params);
            return;
        }
        if (this.params.id != 'result') {
            this.adService.editUserSearchCity(this.currentSearch).subscribe((us) => {
                this.router.navigate(['/app/city', this.params.id]);
            });
        } else {
            this.userService.setCurrentSearchCity(this.currentSearch);
            this.router.navigate(['/app/city/result']);
        }
    }

    cancel() {
        this.router.navigate(['/app/city/', this.params.id]);
    }

    openTuto() {
        const dialogRef = this.dialog.open(TutoComponent, {
            width: '1000px',
            maxWidth: '100%',
            data: {video: 'city_search'}
        });
    }
}
